type Measurement =
    | 'pound'
    | 'ounce'
    | 'teaspoon'
    | 'tablespoon'
    | 'unit'
    | 'cup'
    | 'clove'
    | 'dozen'
    | 'gallon';

export type Ingredient = {
    name: string,
    amount: number | string,
    measurement: Measurement,
    meta?: string,
};

export type Recipe = {
    protein?: 'chicken' | 'pork' | 'steak' | 'tofu' | 'beef',
    image?: string,
    name: string,
    url?: string,
    prepTime: number,
    cookTime: number,
    instructions: {
        section?: string,
        steps: string[],
    }[],
    ingredients: {
        type?: string,
        materials: Ingredient[],
    }[],
};

export const recipes: Recipe[] = [
    {
        name: 'Breakfast Oats',
        prepTime: 0,
        cookTime: 3,
        instructions: [],
        ingredients: [
            {
                materials: [
                    {
                        name: 'milk',
                        amount: 1,
                        measurement: 'gallon',
                    },
                ],
            },
        ],
    },
    {
        name: 'Breakfast Sausage',
        instructions: [],
        prepTime: 5,
        cookTime: 10,
        ingredients: [
            {
                materials: [
                    {
                        name: 'eggs',
                        amount: 1,
                        measurement: 'dozen',
                    },
                    {
                        name: 'orange juice',
                        amount: 1,
                        measurement: 'unit',
                    },
                    {
                        name: 'sausage',
                        amount: 1,
                        measurement: 'unit',
                    },
                    {
                        name: 'hamburger buns',
                        amount: 1,
                        measurement: 'unit',
                    },
                ],
            },
        ],
    },
    {
        name: 'Garlic Parmesan Dry Rub Chicken Wings',
        url: 'https://www.lemonsforlulu.com/garlic-parmesan-dry-rub-wings/',
        protein: 'chicken',
        prepTime: 10,
        cookTime: 30,
        instructions: [
            {
                steps: [
                    'Preheat the oven to 450 degrees. Line a baking sheet with aluminum foil and a wire rack over top.',
                    'Rinse and pat chicken pieces dry. Place the wings in a large bowl and drizzle with oil, tossing each piece so that they all get coated.',
                    'In a bowl, combine the Parmesan cheese, garlic powder, salt, and pepper. Rub spice mixture over the wings.',
                    'Arrange wings on the prepared baking sheet. Bake wings for 30 minutes, turn and bake an additional 10-15 minutes.',
                ],
            },
        ],
        ingredients: [
            {
                type: 'primary',
                materials: [
                    {
                        name: 'chicken wings',
                        amount: 24,
                        measurement: 'unit',
                    },
                    {
                        name: 'oil',
                        amount: 1,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'parmesan cheese',
                        amount: 0.5,
                        measurement: 'cup',
                    },
                    {
                        name: 'garlic powder',
                        amount: 3,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'salt',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'pepper',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                ],
            },
        ],
    },
    {
        name: 'Baked Buffalo Wings',
        url: 'https://www.dinneratthezoo.com/baked-buffalo-wings/',
        protein: 'chicken',
        prepTime: 20,
        cookTime: 45,
        instructions: [
            {
                steps: [
                    'Preheat the oven to 400 degrees. Line a sheet pan with foil and place a non stick oven safe rack on top of the pan.',
                    'Place the chicken wings in a bowl. Add the baking powder, salt, pepper, garlic powder and onion powder.',
                    'Toss to coat the chicken evenly with the baking powder and spices.',
                    'Place the wings on the rack in a single layer.',
                    'Bake for 45 minutes or until wings are light golden brown and crispy.',
                    'In a small bowl, whisk together the hot sauce, butter and honey. ',
                    'Pour the sauce over the wings and toss to coat evenly.',
                    'Serve with celery sticks and ranch dressing.',
                ],
            },
        ],
        ingredients: [
            {
                type: 'primary',
                materials: [
                    {
                        name: 'chicken wings',
                        amount: 3,
                        measurement: 'pound',
                    },
                    {
                        name: 'baking powder',
                        amount: 1,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'salt',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'pepper',
                        amount: 0.5,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'garlic powder',
                        amount: 0.5,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'onion powder',
                        amount: 0.5,
                        measurement: 'teaspoon',
                    },
                    {
                        name: "Frank's red hot sauce",
                        amount: 0.5,
                        measurement: 'cup',
                    },
                    {
                        name: 'butter',
                        meta: 'melted',
                        amount: 4,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'honey',
                        amount: 1,
                        measurement: 'tablespoon',
                    },
                ],
            },
        ],
    },
    {
        name: 'Best Classic Shepherd’s Pie Recipe',
        url: 'https://www.thewholesomedish.com/the-best-classic-shepherds-pie/',
        protein: 'beef',
        prepTime: 20,
        cookTime: 50,
        instructions: [
            {
                section: 'Make the Meat Filling.',
                steps: [
                    'Add the oil to a large skillet and place it over medium-high heat for 2 minutes. Add the onions. Cook 5 minutes, stirring occasionally.',
                    'Add the ground beef (or ground lamb) to the skillet and break it apart with a wooden spoon. Add the parsley, rosemary, thyme, salt, and and pepper. Stir well. Cook for 6-8 minutes, until the meat is browned, stirring occasionally.',
                    'Add the Worcestershire sauce and garlic. Stir to combine. Cook for 1 minute.',
                    'Add the flour and tomato paste. Stir until well incorporated and no clumps of tomato paste remain.',
                    'Add the broth, frozen peas and carrots, and frozen corn. Bring the liquid to a boil then reduce to simmer. Simmer for 5 minutes, stirring occasionally.',
                    'Set the meat mixture aside. Preheat oven to 400 degrees F.',
                ],
            },
            {
                section: 'Make the potato topping.',
                steps: [
                    'Place the potatoes in a large pot. Cover the potatoes with water. Bring the water to a boil. Reduce to a simmer. Cook until potatoes are fork tender, 10-15 minutes.',
                    'Drain the potatoes in a colander. Return the potatoes to the hot pot. Let the potatoes rest in the hot pot for 1 minute to evaporate any remaining liquid.',
                    'Add butter, half & half, garlic powder, salt, and pepper. Mash the potatoes and stir until all the ingredients are mixed together.',
                    'Add the parmesan cheese to the potatoes. Stir until well combined.',
                ],
            },
            {
                section: 'Assemble the casserole.',
                steps: [
                    'Pour the meat mixture into a 9x9 (or 7x11) inch baking dish. Spread it out into an even layer. Spoon the mashed potatoes on top of the meat. Carefully spread into an even layer.',
                    'If the baking dish looks very full, place it on a rimmed baking sheet so that the filling doesn’t bubble over into your oven. Bake uncovered for 25-30 minutes.** Cool for 15 minutes before serving.',
                ],
            },
        ],
        ingredients: [
            {
                type: 'Meat Filling',
                materials: [
                    {
                        name: 'olive oil',
                        amount: 2,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'onion',
                        amount: 1,
                        meta: 'yellow, chopped',
                        measurement: 'cup',
                    },
                    {
                        name: 'ground beef',
                        amount: 1,
                        measurement: 'pound',
                    },
                    {
                        name: 'parsley',
                        amount: 2,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'rosemary',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'thyme',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'salt',
                        amount: 0.5,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'pepper',
                        amount: 0.5,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'worcestershire sauce',
                        amount: 1,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'garlic',
                        meta: 'minced',
                        amount: 2,
                        measurement: 'clove',
                    },
                    {
                        name: 'flour',
                        amount: 2,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'tomato paste',
                        amount: 2,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'beef broth',
                        amount: 1,
                        measurement: 'cup',
                    },
                    {
                        name: 'frozen peas and carrots',
                        amount: 1,
                        measurement: 'cup',
                    },
                    {
                        name: 'frozen corn',
                        amount: 0.5,
                        measurement: 'cup',
                    },
                ],
            },
            {
                type: 'Potato topping',
                materials: [
                    {
                        name: 'potato',
                        meta: 'russet',
                        amount: 2,
                        measurement: 'pound',
                    },
                    {
                        name: 'butter',
                        amount: 8,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'half and half',
                        amount: 0.333,
                        measurement: 'cup',
                    },
                    {
                        name: 'garlic powder',
                        amount: 0.5,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'salt',
                        amount: 0.5,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'pepper',
                        amount: 0.25,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'parmesan cheese',
                        amount: 0.25,
                        measurement: 'cup',
                    },
                ],
            },
        ],
    },
    {
        name: 'Classic Stovetop Beef Stew',
        url: 'https://www.fromvalerieskitchen.com/classic-stovetop-beef-stew/',
        protein: 'beef',
        prepTime: 15,
        cookTime: 120,
        instructions: [
            {
                steps: [
                    'Combine the seasoning mix ingredients in a small bowl. Cut any large pieces of stew meat into smaller bite-size pieces, if necessary.',
                    'Heat the vegetable oil in a large Dutch oven over MEDIUM-HIGH heat. Add about half of the meat to the pan, or as much as you can without overcrowding the pan. Sprinkle 2 teaspoons of the spice mixture over the meat and sauté the beef until nicely browned. Use a slotted spoon to transfer the beef to a dish and set aside. Repeat with the remaining beef, adding additional oil if needed, and seasoning with 2 more teaspoons of the spice mixture. Reserve remaining spice mixture for later.',
                    'Add additional oil to pan if needed and add the onion, celery, and garlic. Cook, stirring occasionally, until softened. Add the tomato paste and stir until well combined. Add the red wine and increase the heat under the pot to bring the mixture to a boil. Add the beef broth, Worcestershire sauce, remaining spice mix and the bay leaf. Add the beef and all the juices that have accumulated back to pot and bring to a boil. Reduce heat to LOW, cover, and simmer for 1-1/2 hours, or until the beef is fork tender.',
                    'Add the potatoes and carrots and raise the heat under the pot to bring the liquid to a boil. Reduce the heat to LOW, cover and simmer for another 30 to 40 minutes until the veggies are fork tender. Stir in the frozen peas and parsley. Combine the water and cornstarch in a measuring cup or small bowl and stir it into the beef stew. Continue cooking, uncovered, until thickened.',
                    'Discard the bay leaf before serving.',
                ],
            },
        ],
        ingredients: [
            {
                type: 'Beef Stew Seasoning Mix',
                materials: [
                    {
                        name: 'sea salt',
                        amount: 1.5,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'rosemary',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'thyme',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'marjoram',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'paprika',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'pepper',
                        amount: 0.5,
                        measurement: 'teaspoon',
                    },
                ],
            },
            {
                type: 'Beef Stew',
                materials: [
                    {
                        name: 'vegetable oil',
                        amount: 2,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'beef stew meat',
                        amount: 2,
                        measurement: 'pound',
                    },
                    {
                        name: 'onion',
                        meta: 'yellow or white, diced',
                        amount: 1,
                        measurement: 'cup',
                    },
                    {
                        name: 'garlic',
                        meta: 'minced',
                        amount: 1,
                        measurement: 'teaspoon',
                    },
                    {
                        name: 'tomato paste',
                        amount: 3,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'wine',
                        meta: 'cabernet, zinfandel, merlot',
                        amount: 1,
                        measurement: 'cup',
                    },
                    {
                        name: 'beef broth',
                        amount: 2,
                        measurement: 'cup',
                    },
                    {
                        name: 'worcestershire sauce',
                        amount: 1,
                        measurement: 'tablespoon',
                    },
                    {
                        name: 'bay leaf',
                        amount: 1,
                        measurement: 'unit',
                    },
                    {
                        name: 'potato',
                        meta: 'baby yellow, red, halved or quartered',
                        amount: 1,
                        measurement: 'pound',
                    },
                    {
                        name: 'carrot',
                        meta: 'peeled and chopped',
                        amount: 3,
                        measurement: 'unit',
                    },
                    {
                        name: 'frozen peas',
                        amount: 1,
                        measurement: 'cup',
                    },
                    {
                        name: 'water',
                        amount: 0.25,
                        measurement: 'cup',
                    },
                    {
                        name: 'cornstarch',
                        amount: 2,
                        measurement: 'tablespoon',
                    },
                ],
            },
        ],
    },
];
